import {ActivatedRouteSnapshot, Router, Routes} from '@angular/router';
import { TwoColumnsRightComponent } from '@fry/upgrade/main.template.directive';
import {
  AllEventsComponent,
  ApplyTagsComponent,
  EventsComponent,
  EventSectionComponent,
  EventSectionViewComponent,
  EventViewComponent,
  ManageTimelinesComponent,
  NewEventComponent,
  SelectTagsComponent,
  TearoffEventViewComponent
} from '@fry/upgrade/events.directive';
import { setupGuard } from '@fry/upgrade/setup.guard';
import { authGuard, forceOnlineGuard } from '@fry-it/risr-core';
import { inject } from '@angular/core';
import { unsavedGuard } from '@fry/upgrade/unsaved.guard';
import { autosaveGuard } from '@fry/upgrade/autosave.guard';

export const eventsRoutes: Routes = [
  {
    path: 'events',
    component: TwoColumnsRightComponent,
    canActivate: [setupGuard],
    children: [
      {
        path: 'list',
        component: EventsComponent,
        data: {
          angularJsComponent: 'events',
          showActionButtons: true,
          state: 'epf.events.index',
          permission: 'events.view.own'
        },
        canActivate: [authGuard]
      },
      {
        path: 'list/:filterId',
        component: EventsComponent,
        data: {
          angularJsComponent: 'events',
          showActionButtons: true,
          state: 'epf.events.index',
          permission: 'events.view.own'
        },
        canActivate: [authGuard]
      },
      {
        path: 'select',
        component: SelectTagsComponent,
        data: {
          angularJsTemplate: 'app/components/events/selecttags.html',
          angularJsController: 'SelectTagsController',
          angularJsControllerAs: 'eventsCtrl',
          state: 'epf.events.selecttags'
        },
        canActivate: [forceOnlineGuard]
      },
      {
        path: 'apply',
        component: ApplyTagsComponent,
        data: {
          angularJsTemplate: 'app/components/events/applytags.html',
          angularJsController: 'ApplyTagsController',
          angularJsControllerAs: 'eventsCtrl',
          state: 'epf.events.applytags'
        },
        canActivate: [forceOnlineGuard]
      },
      {
        path: 'manage',
        component: ManageTimelinesComponent,
        data: {
          angularJsTemplate: 'app/components/events/manage-timelines.html',
          angularJsController: 'ManageTimelinesController',
          angularJsControllerAs: 'ctrl',
          state: 'epf.events.manage-timelines',
          permission: 'organisation.manage-timelines'
        },
        canActivate: [forceOnlineGuard, authGuard],
        canDeactivate: [unsavedGuard]
      },
      {
        path: 'new',
        component: NewEventComponent,
        data: {
          angularJsTemplate: 'app/components/events/new.html',
          angularJsController: 'NewEventController',
          angularJsControllerAs: 'newEventCtrl',
          state: 'epf.events.new'
        }
      },
      {
        path: 'new/:user',
        component: NewEventComponent,
        data: {
          angularJsTemplate: 'app/components/events/new.html',
          angularJsController: 'NewEventController',
          angularJsControllerAs: 'newEventCtrl',
          state: 'epf.events.new'
        }
      },
      {
        path: 'view/:eventId',
        component: EventViewComponent,
        data: {
          angularJsTemplate: 'app/components/events/view.html',
          angularJsController: 'EventViewController',
          angularJsControllerAs: 'eventViewCtrl',
          state: 'epf.events.view'
        }
      },
      {
        path: 'view/:eventId/:scrollTo',
        component: EventViewComponent,
        data: {
          angularJsTemplate: 'app/components/events/view.html',
          angularJsController: 'EventViewController',
          angularJsControllerAs: 'eventViewCtrl',
          state: 'epf.events.view'
        }
      },
      {
        path: 'tearoff/:eventId',
        component: TearoffEventViewComponent,
        data: {
          angularJsTemplate: 'app/components/events/tearoff.html',
          angularJsController: 'EventViewController',
          angularJsControllerAs: 'eventViewCtrl',
          state: 'epf.events.tearoff',
        }
      },
      {
        path: 'new-section/:eventTypeId',
        component: EventSectionComponent,
        canDeactivate: [unsavedGuard, autosaveGuard],
        data: {
          angularJsTemplate: 'app/components/events/eventsection.edit.html',
          angularJsController: 'SectionController',
          angularJsControllerAs: 'eventCtrl',
          action: 'new',
          state: 'epf.events.section-new'
        },
        resolve: {
          fillin: (route: ActivatedRouteSnapshot) => {
            const router = inject(Router);
            const eventSectionId = route.queryParamMap.get('doc');
            if (eventSectionId) {
              router.navigate(['events', 'fillin', eventSectionId]);
              return false;
            }
            return true;
          }
        }
      },
      {
        path: 'new-section/:eventTypeId/:user',
        component: EventSectionComponent,
        canDeactivate: [unsavedGuard, autosaveGuard],
        data: {
          angularJsTemplate: 'app/components/events/eventsection.edit.html',
          angularJsController: 'SectionController',
          angularJsControllerAs: 'eventCtrl',
          action: 'new',
          state: 'epf.events.section-new'
        },
        resolve: {
          fillin: (route: ActivatedRouteSnapshot) => {
            const router = inject(Router);
            const eventSectionId = route.queryParamMap.get('doc');
            if (eventSectionId) {
              router.navigate(['events', 'fillin', eventSectionId]);
              return false;
            }
            return true;
          }
        }
      },
      {
        path: 'fillin/:eventSectionId/:user',
        component: EventSectionComponent,
        canDeactivate: [unsavedGuard, autosaveGuard],
        data: {
          angularJsTemplate: 'app/components/events/eventsection.edit.html',
          angularJsController: 'SectionController',
          angularJsControllerAs: 'eventCtrl',
          action: 'edit',
          state: 'epf.events.section-fillin'
        }
      },
      {
        path: 'fillin/:eventSectionId',
        component: EventSectionComponent,
        canDeactivate: [unsavedGuard, autosaveGuard],
        data: {
          angularJsTemplate: 'app/components/events/eventsection.edit.html',
          angularJsController: 'SectionController',
          angularJsControllerAs: 'eventCtrl',
          action: 'edit',
          state: 'epf.events.section-fillin'
        }
      },
      {
        path: 'view-section/:eventSectionId',
        component: EventSectionViewComponent,
        data: {
          angularJsTemplate: 'app/components/events/eventsection.view.html',
          angularJsController: 'EventSectionViewController',
          angularJsControllerAs: 'eventViewCtrl',
          state: 'epf.events.section-view'
        }
      },
      {
        path: 'view-section/:eventSectionId/:user',
        component: EventSectionViewComponent,
        data: {
          angularJsTemplate: 'app/components/events/eventsection.view.html',
          angularJsController: 'EventSectionViewController',
          angularJsControllerAs: 'eventViewCtrl',
          state: 'epf.events.section-view'
        }
      }
    ]
  },
  {
    path: 'allevents',
    component: TwoColumnsRightComponent,
    children: [
      {
        path: '',
        component: AllEventsComponent,
        data: {
          angularJsTemplate: 'app/components/events/allevents.html',
          angularJsController: 'AllEventsController',
          state: 'epf.allevents.index',
          angularJsControllerAs: 'eventsCtrl',
          showActionButtons: true,
          permission: 'events.view'
        },
        canActivate: [setupGuard, forceOnlineGuard, authGuard]
      }
    ]
  }
];
